import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getConfig } from "@edx/frontend-platform";
import {
  sendPageEvent,
  sendTrackEvent,
} from "@edx/frontend-platform/analytics";
import { getAuthService } from "@edx/frontend-platform/auth";
import { useIntl } from "@edx/frontend-platform/i18n";
import { Icon, Tab, Tabs, Button, Image } from "@openedx/paragon";
import { ChevronLeft } from "@openedx/paragon/icons";
import PropTypes from "prop-types";
import { Navigate, useNavigate } from "react-router-dom";

import BaseContainer from "../base-container";
import { clearThirdPartyAuthContextErrorMessage } from "../common-components/data/actions";
import { tpaProvidersSelector } from "../common-components/data/selectors";
import messages from "../common-components/messages";
import { LOGIN_PAGE, REGISTER_PAGE } from "../data/constants";
import {
  getTpaHint,
  getTpaProvider,
  updatePathWithQueryParams,
} from "../data/utils";
import { backupLoginForm } from "../login/data/actions";
import LoginComponentSlot from "../plugin-slots/LoginComponentSlot";
import { RegistrationPage } from "../register";
import { backupRegistrationForm } from "../register/data/actions";

import logo from "../assets/logo.png";

const ClIENT_URL = "https://deneyap.milliteknolojiakademisi.gov.tr/";

// Özel CSS (Inline olarak ekliyoruz, dilersen ayrı bir scss dosyasına taşıyabilirsin)
const styles = {
  headerWrapper: {
    marginBottom: "0",
  },
  title: {
    fontWeight: "400",
    marginBottom: "0.5rem",
    fontSize: "2rem", // Başlığı biraz büyüttük
  },
  subTitle: {
    color: "#6c757d", // Bootstrap text-muted rengi
    fontSize: "0.9rem",
    fontWeight: "300",
    maxWidth: '450px',
  },
  bottomNavWrapper: {
    marginTop: "1rem",
    textAlign: "center",
    borderTop: "1px solid #e9ecef", // İsteğe bağlı ince çizgi
    padding: "1.5rem 0",
  },
  actionLink: {
    fontWeight: "bold",
    textDecoration: "underline",
    marginLeft: "5px",
    color: "#2F6FEC",
    fontSize: "1rem",
    fontWeight: "400",
  },
};

const Logistration = ({ selectedPage }) => {
  const tpaHint = getTpaHint();
  const tpaProviders = useSelector(tpaProvidersSelector);
  const dispatch = useDispatch();
  const { providers, secondaryProviders } = tpaProviders;
  const { formatMessage } = useIntl();
  const [institutionLogin, setInstitutionLogin] = useState(false);
  const [key, setKey] = useState("");
  const navigate = useNavigate();
  const disablePublicAccountCreation =
    getConfig().ALLOW_PUBLIC_ACCOUNT_CREATION === false;
  const hideRegistrationLink = getConfig().SHOW_REGISTRATION_LINKS === false;

  useEffect(() => {
    const authService = getAuthService();
    if (authService) {
      authService.getCsrfTokenService().getCsrfToken(getConfig().LMS_BASE_URL);
    }
  });

  useEffect(() => {
    // Butonları full-width yapmak için global bir stil ekleyelim
    // Bu sadece bu sayfa render olduğunda çalışır
    const style = document.createElement("style");
    style.innerHTML = `
      .login-form .btn-primary, 
      .registration-form .btn-primary, 
      .form-actions .btn { 
        width: 100%; 
        display: block; 
        padding: 0.75rem;
        font-size: 1.1rem;
        font-weight: 600;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (disablePublicAccountCreation) {
      navigate(updatePathWithQueryParams(LOGIN_PAGE));
    }
  }, [navigate, disablePublicAccountCreation]);

  const handleInstitutionLogin = (e) => {
    sendTrackEvent("edx.bi.institution_login_form.toggled", {
      category: "user-engagement",
    });
    if (typeof e === "string") {
      sendPageEvent(
        "login_and_registration",
        e === "/login" ? "login" : "register",
      );
    } else {
      sendPageEvent("login_and_registration", e.target.dataset.eventName);
    }

    setInstitutionLogin(!institutionLogin);
  };

  const handleOnSelect = (tabKey, currentTab) => {
    if (tabKey === currentTab) {
      return;
    }
    sendTrackEvent(`edx.bi.${tabKey.replace("/", "")}_form.toggled`, {
      category: "user-engagement",
    });
    dispatch(clearThirdPartyAuthContextErrorMessage());
    if (tabKey === LOGIN_PAGE) {
      dispatch(backupRegistrationForm());
    } else if (tabKey === REGISTER_PAGE) {
      dispatch(backupLoginForm());
    }
    setKey(tabKey);
  };

  const tabTitle = (
    <div className="d-flex">
      <Icon src={ChevronLeft} className="left-icon" />
      <span className="ml-2">
        {selectedPage === LOGIN_PAGE
          ? formatMessage(messages["logistration.sign.in"])
          : formatMessage(messages["logistration.register"])}
      </span>
    </div>
  );

  const isValidTpaHint = () => {
    const { provider } = getTpaProvider(tpaHint, providers, secondaryProviders);
    return !!provider;
  };

  const renderHeader = () => {
    if (selectedPage === LOGIN_PAGE) {
      return (
        <div style={styles.headerWrapper} className="main-content">
          <h2 style={styles.title} className="fw-light">
            {formatMessage(messages["logistration.sign.in"])}
          </h2>
          <p style={styles.subTitle}>
            {formatMessage(messages["logistration.login.title.text"])}
          </p>
        </div>
      );
    }
    return (
      <div style={styles.headerWrapper} className="main-content">
        <h2 style={styles.title}>
          {formatMessage(messages["logistration.register"])}
        </h2>
        <p style={styles.subTitle}>
          {formatMessage(messages["logistration.register.title.text"])}
        </p>
      </div>
    );
  };

  const renderBottomNavigation = () => {
    if (selectedPage === LOGIN_PAGE && !hideRegistrationLink) {
      return (
        <div style={styles.bottomNavWrapper}>
          <span className="text-muted text-primary" style={styles.subTitle}>
            {formatMessage(messages["logistration.login.notice.text"])}
          </span>
          <Button
            variant="link"
            style={styles.actionLink}
            className="p-0 align-baseline"
            onClick={() => handleOnSelect(REGISTER_PAGE, selectedPage)}
          >
            {formatMessage(messages["logistration.register"])}
          </Button>
        </div>
      );
    }
    if (selectedPage === REGISTER_PAGE) {
      return (
        <div style={styles.bottomNavWrapper}>
          <span className="text-muted" style={styles.subTitle}>
            {formatMessage(messages["logistration.register.notice.text"])}
          </span>
          <Button
            variant="link"
            style={styles.actionLink}
            className="p-0 align-baseline"
            onClick={() => handleOnSelect(LOGIN_PAGE, selectedPage)}
          >
            {formatMessage(messages["logistration.sign.in"])}
          </Button>
        </div>
      );
    }
    return null;
  };

  return (
    <BaseContainer>
      <div>
        <div className="text-center mt-3 mb-5">
          <a
            href={
              ClIENT_URL || "https://deneyap.milliteknolojiakademisi.gov.tr/"
            }
          >
            <Image
              src={logo}
              alt="Milli Teknoloji Akademisi"
              style={{ maxHeight: "60px" }}
            />
          </a>
        </div>
        {disablePublicAccountCreation ? (
          <>
            {institutionLogin && (
              <Tabs
                defaultActiveKey=""
                id="controlled-tab"
                onSelect={handleInstitutionLogin}
              >
                <Tab title={tabTitle} eventKey={LOGIN_PAGE} />
              </Tabs>
            )}
            <div id="main-content" className="main-content">
              {!institutionLogin && (
                <h3 className="mb-4.5">
                  {formatMessage(messages["logistration.sign.in"])}
                </h3>
              )}
              <LoginComponentSlot
                institutionLogin={institutionLogin}
                handleInstitutionLogin={handleInstitutionLogin}
              />
            </div>
          </>
        ) : (
          <div>
            {institutionLogin ? (
              <Tabs
                defaultActiveKey=""
                id="controlled-tab"
                onSelect={handleInstitutionLogin}
              >
                <Tab
                  title={tabTitle}
                  eventKey={
                    selectedPage === LOGIN_PAGE ? LOGIN_PAGE : REGISTER_PAGE
                  }
                />
              </Tabs>
            ) : (
              // Gelişmiş Header Yapısı (Başlık + Açıklama)
              !isValidTpaHint() && renderHeader()
            )}

            {key && <Navigate to={updatePathWithQueryParams(key)} replace />}

            <div id="main-content" className="main-content">
              {selectedPage === LOGIN_PAGE ? (
                <div className="login-form-wrapper">
                  <LoginComponentSlot
                    institutionLogin={institutionLogin}
                    handleInstitutionLogin={handleInstitutionLogin}
                  />
                </div>
              ) : (
                <div className="registration-form-wrapper">
                  <RegistrationPage
                    institutionLogin={institutionLogin}
                    handleInstitutionLogin={handleInstitutionLogin}
                  />
                </div>
              )}

              {/* Alt Kısım Navigasyonu */}
              {!institutionLogin && renderBottomNavigation()}
            </div>
          </div>
        )}
      </div>
    </BaseContainer>
  );
};

Logistration.propTypes = {
  selectedPage: PropTypes.string,
};

Logistration.defaultProps = {
  selectedPage: REGISTER_PAGE,
};

export default Logistration;
